import type { ReactNode } from 'react';
import clsx from 'clsx';

export type MenuItemProps = {
  children: ReactNode;
  icon?: ReactNode;
  description?: string;
  badge?: ReactNode;
  active?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
};

export type MenuListProps = {
  children: ReactNode;
  className?: string;
};

export type MenuGroupProps = {
  children: ReactNode;
  label?: string;
  className?: string;
};

function MenuList({ children, className }: MenuListProps) {
  return (
    <div
      className={clsx(
        'bg-white dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-600 overflow-hidden',
        className
      )}
    >
      {children}
    </div>
  );
}

function MenuItem({
  children,
  icon,
  description,
  badge,
  active = false,
  disabled = false,
  onClick,
  className,
}: MenuItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors',
        active
          ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
          : 'hover:bg-gray-50 dark:hover:bg-dark-700',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      {icon && (
        <span
          className={clsx(
            'shrink-0',
            active ? 'text-primary-500 dark:text-primary-400' : 'text-gray-400 dark:text-dark-400'
          )}
        >
          {icon}
        </span>
      )}
      <div className="flex-1 min-w-0">
        <span
          className={clsx(
            'font-medium',
            active ? 'text-primary-700 dark:text-primary-300' : 'text-gray-900 dark:text-dark-100'
          )}
        >
          {children}
        </span>
        {description && (
          <p className="text-sm text-gray-500 dark:text-dark-400 truncate">{description}</p>
        )}
      </div>
      {badge && <span className="shrink-0">{badge}</span>}
    </button>
  );
}

function MenuGroup({ children, label, className }: MenuGroupProps) {
  return (
    <div className={className}>
      {label && (
        <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-dark-400 bg-gray-50 dark:bg-dark-700/50">
          {label}
        </div>
      )}
      {children}
    </div>
  );
}

function MenuDivider() {
  return <div className="h-px bg-gray-200 dark:bg-dark-600" />;
}

MenuList.displayName = 'MenuList';
MenuItem.displayName = 'MenuItem';
MenuGroup.displayName = 'MenuGroup';
MenuDivider.displayName = 'MenuDivider';

export { MenuList, MenuItem, MenuGroup, MenuDivider };
